import { TravelCurrencyCode, useTravelRateComparisonsForTravelPlacesQuery, PriceComparisonFragmentDoc, PriceComparisonFragment } from '@graphql/generated';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useEffect } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { translatedPrice } from '@shared/utils/accommodationPrice';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { formatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';

const queryStatusVar = makeVar<{
  loading: boolean;
  error?: ApolloError;
}>({ loading: false, error: undefined });

export const useTravelPlacesPriceComparison = (joyPlaceIds: string[]) => {
  const { eventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString;
  const { checkIn: checkInDate, checkOut: checkOutDate } = useCheckInCheckoutDates({ eventDate });
  const [localeBasedCurrencyEnabled, isLocaleCurrencyFlagLoading] = useIsFeatureEnabled('localeBasedHotelCurrency');
  const [showNewPriceComparisonUI] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');
  const [currencyCode, { loading: isCurrencyCodeLoading }] = useTravelWhiteLabelCurrency();
  const placeIds = joyPlaceIds.map(id => ({ joyPlaceId: id }));
  const { data, loading, error } = useTravelRateComparisonsForTravelPlacesQuery({
    batchMode: 'fast',
    variables: {
      payload: {
        currencyCode: localeBasedCurrencyEnabled && currencyCode ? currencyCode : TravelCurrencyCode.USD,
        dates: {
          checkIn: {
            high: checkInDate,
            low: checkInDate
          },
          checkOut: {
            high: checkOutDate,
            low: checkOutDate
          }
        },
        occupancies: {
          occupancies: [
            {
              adults: 2,
              childrenAges: []
            }
          ],
          unitCount: 1
        },
        placeIds
      }
    },
    skip: !placeIds.length || isLocaleCurrencyFlagLoading || isCurrencyCodeLoading || !showNewPriceComparisonUI,
    onError(error) {
      addError(error, {
        source: 'Price Comparison api failed',
        context: 'travelRateComparisonsForTravelPlaces',
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        currencyCode: localeBasedCurrencyEnabled && currencyCode ? currencyCode : TravelCurrencyCode.USD,
        joyPlaceIds: placeIds
      });
    }
  });

  useEffect(() => {
    queryStatusVar({
      loading,
      error
    });
  }, [error, loading]);

  return {
    data,
    loading,
    error
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, joyPlaceId: string) => {
  const fragment = PriceComparisonFragmentDoc;
  const id = `TravelPlaceComparison:${joyPlaceId}`;
  const data = client.cache.readFragment<PriceComparisonFragment>({
    id,
    fragment
  });

  return data;
};

export const useTravelPlacesPriceComparisonFragment = (
  travelPlaceId: string,
  skip?: boolean
): {
  rates?: {
    provider: string;
    price: string;
    priceInMinorUnits: number;
    url: string;
  }[];
  loading: boolean;
  error?: Error;
} => {
  const client = useApolloClient();
  const locale = useLocaleContext();
  const { loading, error } = useReactiveVar(queryStatusVar);
  const [redirectServiceEnabled] = useIsFeatureEnabled('guestLinkUseRedirectService');

  if (loading) {
    return { rates: undefined, loading: true };
  }
  if (error) {
    return { rates: [], loading: false, error: error };
  }
  if (skip) {
    return { rates: undefined, loading: false };
  }

  const data = readFragmentFromCache(client, travelPlaceId);

  const rates =
    data?.providers?.map(({ providerFQDN, providerSearchURL, nightlyBaseRate }) => ({
      provider: providerFQDN,
      price: translatedPrice(nightlyBaseRate.valueInMinorUnits, nightlyBaseRate.digits, nightlyBaseRate.currency, locale.language),
      priceInMinorUnits: nightlyBaseRate.valueInMinorUnits,
      url: redirectServiceEnabled ? formatRedirectURL({ url: providerSearchURL }) ?? '' : providerSearchURL
    })) ?? [];

  return { rates, loading: loading };
};
